import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useConfigStore } from '@/stores/config';
import { usePermissions } from '@/composables/permissions';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/dashboard',
    component: () => import('@/layouts/default-layout/DefaultLayout.vue'),
    meta: {
      middleware: 'auth',
    },
    children: [
      {
        path: '/dashboard',
        name: 'dashboard',
        component: () => import('@/views/Dashboard.vue'),
        meta: {
          pageTitle: 'Dashboard',
          permission: ''
        },
      },
      {
        path: '/advanced-dashboard',
        name: 'advanced-dashboard',
        component: () => import('@/views/AdvancedDashboard.vue'),
        meta: {
          pageTitle: 'AdvancedDashboard',
          // The reporting dashboard (src/views/AdvancedDashboard.vue) renders its own toolbar
          // (DashboardDateRangePicker) and collapsible section headers in place of the
          // generic page title bar, so that bar is suppressed here — see PageTitle.vue,
          // which reads this flag.
          hidePageTitle: true,
          permission: 'behandam.admin.v1.dashboard.supervisor'
        },
      },
      {
        path: '/welcome',
        name: 'welcome',
        component: () => import('@/views/Welcome.vue'),
        meta: {
          permission: ''
        },
      },
      {
        path: '/sentry-test',
        name: 'sentry-test',
        component: () => import('@/views/SentryTest.vue'),
        meta: {
          pageTitle: 'Sentry Test',
          breadcrumbs: ['Sentry Test'],
          permission: ''
        },
      },
      {
        path: '/heroes',
        children: [
          {
            path: '/heroes',
            name: 'heroes',
            component: () => import('@/views/hero/Heroes.vue'),
            meta: {
              pageTitle: 'heroes',
              permission: 'behandam.admin.v1.heroes.view'
            },
          },
          {
            path: '/heroes/:id',
            name: 'edit-hero',
            component: () => import('@/views/hero/EditHero.vue'),
            meta: {
              pageTitle: 'editHero',
              backLink: true,
              backLinkName: 'heroes',
              permission: 'behandam.admin.v1.heroes.update'
            },
          },
          {
            path: '/heroes/add',
            name: 'create-hero',
            component: () => import('@/views/hero/AddHero.vue'),
            meta: {
              pageTitle: 'addhero',
              backLink: true,
              backLinkName: 'heroes',
              permission: 'behandam.admin.v1.heroes.create'
            },
          },
        ],
      },
      {
        path: '/leads',
        children: [
          {
            path: 'bank',
            name: 'bank-numbers',
            component: () => import('@/views/leads/BankNumbers.vue'),
            meta: {
              pageTitle: 'bankNumbers',
              permission: 'behandam.admin.v1.leads.supervisor'
            },
          },
          {
            path: 'mine',
            name: 'my-numbers',
            component: () => import('@/views/leads/MyNumbers.vue'),
            meta: {
              pageTitle: 'myNumbers',
              permission: 'behandam.admin.v1.leads.view'
            },
          },
          {
            path: 'systemic',
            name: 'systemic-leads',
            component: () => import('@/views/leads/Systemic.vue'),
            meta: {
              pageTitle: 'systemicLeads',
              permission: 'behandam.admin.v1.leads.view'
            },
          },
          {
            path: 'latest-issue',
            name: 'latest-issue-numbers',
            component: () => import('@/views/leads/latestIssues.vue'),
            meta: {
              pageTitle: 'latestIssues',
              permission: 'behandam.admin.v1.leads.supervisor'
            },
          },
        ],
      },
      {
        path: '/contacts',
        children: [
          {
            path: 'calls',
            children: [
              {
                path: '',
                name: 'calls',
                component: () => import('@/views/contacts/Calls.vue'),
                meta: {
                  pageTitle: 'calls',
                  permission: 'behandam.admin.v1.support-calls.supervisor'
                },
              },
              {
                path: 'mine',
                name: 'myCalls',
                component: () => import('@/views/contacts/MyCalls.vue'),
                meta: {
                  pageTitle: 'myCalls',
                  permission: 'behandam.admin.v1.support-calls.view'
                },
              },
              {
                path: 'create',
                name: 'createCall',
                component: () => import('@/views/contacts/CreateCall.vue'),
                meta: {
                  pageTitle: 'callsMakeCall',
                  permission: 'report.admin.v1.support-calls.creatable-by-type.view'
                },
              },
            ]
          },
        ],
      },
      {
        path: 'leads/request',
        name: 'requestNumber',
        component: () => import('@/views/contacts/RequestNumber.vue'),
        meta: {
          pageTitle: 'requestNumber',
          permission: 'behandam.admin.v1.leads.request-assignment.create'
        },
      },
      {
        path: '/notifications',
        children: [
          {
            path: '',
            name: 'notifications',
            component: () => import('@/views/notifications/Notifications.vue'),
            meta: {
              pageTitle: 'notifications',
              permission: 'behandam.admin.v1.support.calls.view'
            },
          },
          {
            path: 'product-post-schedule',
            name: 'ProductPostSchedule',
            component: () => import('@/views/notifications/ProductPosts.vue'),
            meta: {
              pageTitle: 'ProductPostSchedule',
              permission: 'content.admin.v1.product-post-schedule.view'
            },
          },
          {
            path: 'bulk',
            name: 'BulkNotifications',
            component: () => import('@/views/notifications/BulkNotifications.vue'),
            meta: {
              pageTitle: 'ارسال گروهی',
              permission: 'notification.admin.v1.bulk-campaigns.view'
            },
          },
        ],
      },
      {
        path: '/command-requests',
        name: 'commandRequests',
        component: () => import('@/views/command-request/list/index.vue'),
        meta: {
          pageTitle: 'commandRequests',
          permission: 'workflow.admin.v1.command-requests.view'
        },
      },
      {
        path: '/new-food-list',
        component: () => import('@/layouts/Food.vue'),
        children: [
          {
            path: 'foods/add',
            name: 'addFood',
            component: () => import('@/views/new-food-list/foods/AddFood.vue'),
            meta: {
              pageTitle: 'addFood',
              permission: 'food.admin.v1.foods.create'
            },
          },
          {
            path: 'foods/:id/edit',
            name: 'editFood',
            component: () => import('@/views/new-food-list/foods/EditFood.vue'),
            meta: {
              pageTitle: 'editFood',
              permission: 'food.admin.v1.foods.update'
            },
          },
          {
            path: 'foods/:id/diets',
            name: 'foodDiets',
            component: () => import('@/views/new-food-list/foods/FoodDiet.vue'),
            meta: {
              pageTitle: 'diets',
              permission: 'food.admin.v1.foods.update'
            },
          },
          {
            path: 'foods/:id/meals',
            name: 'foodMeals',
            component: () => import('@/views/new-food-list/foods/FoodMeal.vue'),
            meta: {
              pageTitle: 'meals',
              permission: 'food.admin.v1.foods.update'
            },
          },
          {
            path: 'foods/:id/items',
            name: 'foodItemList',
            component: () => import('@/views/new-food-list/foods/FoodItem.vue'),
            meta: {
              pageTitle: 'foodItems',
              permission: 'food.admin.v1.foods.update'
            },
          }
        ],
      },
      {
        path: '/new-food-list',
        children: [
          {
            path: 'foods',
            name: 'foods',
            component: () => import('@/views/new-food-list/foods/Foods.vue'),
            meta: {
              pageTitle: 'foods',
              permission: 'food.admin.v1.foods.view'
            },
          },
          {
            path: 'foods/tags',
            name: 'foodTags',
            component: () => import('@/views/new-food-list/foods/FoodTags.vue'),
            meta: {
              pageTitle: 'tags',
              permission: 'categorization.admin.v1.tags.view'
            },
          },
          {
            path: 'foods/categories',
            name: 'foodCategories',
            component: () => import('@/views/new-food-list/foods/Category.vue'),
            meta: {
              pageTitle: 'categories',
              permission: 'categorization.admin.v1.categories.view'
            },
          },
          {
            path: 'meals',
            name: 'meals',
            component: () => import('@/views/new-food-list/Meals.vue'),
            meta: {
              pageTitle: 'meals',
              permission: 'food.admin.v1.meals.view'
            },
          },
          {
            path: 'food-items',
            name: 'foodItems',
            component: () => import('@/views/new-food-list/foodItems/FoodItems.vue'),
            meta: {
              pageTitle: 'foodItems',
              permission: 'food.admin.v1.food-items.view'
            },
          },
          {
            path: 'food-items/add',
            name: 'addFoodItem',
            component: () => import('@/views/new-food-list/foodItems/AddFoodItem.vue'),
            meta: {
              pageTitle: 'addFoodItem',
              backLink: true,
              backLinkName: 'foodItems',
              permission: 'food.admin.v1.food-items.create'
            },
          },
          {
            path: 'food-items/:id/edit',
            name: 'editFoodItem',
            component: () => import('@/views/new-food-list/foodItems/EditFoodItem.vue'),
            meta: {
              pageTitle: 'editFoodItem',
              backLink: true,
              backLinkName: 'foodItems',
              permission: 'food.admin.v1.food-items.update'
            },
          },
          {
            path: 'food-menus',
            name: 'food-menus',
            component: () => import('@/views/new-food-list/food-menus/FoodMenus.vue'),
            meta: {
              pageTitle: 'foodMenus',
              permission: 'food.admin.v1.menus.view'
            },
          },
          {
            path: 'food-menus/:id',
            name: 'food-menu-edit',
            component: () => import('@/views/new-food-list/food-menus/EditFoodMenu.vue'),
            meta: {
              pageTitle: 'editFoodMenu',
              backLink: true,
              backLinkName: 'food-menus',
              permission: 'food.admin.v1.menus.update'
            }
          },
          {
            path: 'food-menus/tags',
            name: 'food-menus-tags',
            component: () => import('@/views/new-food-list/food-menus/Tags.vue'),
            meta: {
              pageTitle: 'tags',
              permission: 'categorization.admin.v1.tags.view'
            },
          },
          {
            path: 'food-menus/categories',
            name: 'foodMenusCategories',
            component: () => import('@/views/new-food-list/food-menus/Category.vue'),
            meta: {
              pageTitle: 'categories',
              permission: 'categorization.admin.v1.categories.view'
            },
          },
          {
            path: 'food-replacement-report',
            name: 'food-replacement-report',
            component: () => import('@/views/new-food-list/FoodReplacementReport.vue'),
            meta: {
              pageTitle: 'foodReplacementReport',
              permission: 'behandam.admin.v1.audits.view'
            }
          },
          {
            path: 'fasting-pattern',
            name: 'fasting-pattern',
            component: () => import('@/views/new-food-list/fasting-pattern/FastingPattern.vue'),
            meta: {
              pageTitle: 'fastingPattern',
              breadcrumbs: ['fastingPattern'],
              permission: 'food.admin.v1.fasting-patterns.view'
            },
          },
          {
            path: 'fasting-pattern/:id/meals',
            name: 'fasting-pattern-meals',
            component: () => import('@/views/new-food-list/fasting-pattern/FastingPatternMeal.vue'),
            meta: {
              pageTitle: 'fastingPatternMeals',
              breadcrumbs: ['fastingPattern', 'fastingPatternMeals'],
              backLink: true,
              backLinkName: 'fastingPattern',
              permission: 'food.admin.v1.fasting-pattern-meals.view'
            }
          },
          {
            path: 'waiting-list',
            name: 'waitingList',
            component: () => import('@/views/new-food-list/waiting-list/WaitingList.vue'),
            meta: {
              pageTitle: 'waitingList',
              permission: 'behandam.admin.v1.term-menus.view'
            },
          }
        ],
      },
      {
        path: '/payments',
        children: [
          {
            path: 'discounts',
            name: 'payments-discounts',
            component: () => import('@/views/payments/discounts/Discounts.vue'),
            meta: {
              pageTitle: 'discounts',
              permission: 'order.admin.v1.discounts.view'
            },
          },
          {
            path: 'discounts/add',
            name: 'payments-discounts-add',
            component: () => import('@/views/payments/discounts/AddDiscount.vue'),
            meta: {
              pageTitle: 'addDiscount',
              backLink: true,
              backLinkName: 'payments-discounts',
              noHeader: true,
              permission: 'order.admin.v1.discounts.create'
            },
          },
          {
            path: 'discounts/:id/edit',
            name: 'payments-discounts-edit',
            component: () => import('@/views/payments/discounts/EditDiscount.vue'),
            meta: {
              pageTitle: 'editDiscount',
              backLink: true,
              backLinkName: 'payments-discounts',
              noHeader: true,
              permission: 'order.admin.v1.discounts.update'
            },
          },
          {
            path: 'orders',
            name: 'payments-orders',
            component: () => import('@/views/payments/orders/index.vue'),
            meta: {
              pageTitle: 'orders',
              permission: 'order.admin.v1.orders.supervisor'
            },
          },
          {
            path: 'orders/:orderId',
            name: 'order',
            component: () => import('@/views/payments/orders/order.vue'),
            meta: {
              pageTitle: 'orderDetails',
              permission: 'order.admin.v1.orders.supervisor'
            },
          },
          {
            path: 'payment-accounts',
            name: 'payments-accounts',
            component: () => import('@/views/payments/PaymentAccounts.vue'),
            meta: {
              pageTitle: 'paymentAccounts',
              permission: 'payment.admin.v1.payment-accounts.supervisor'
            },
          },
          {
            path: 'allocation-rules',
            name: 'payments-allocation-rules',
            component: () => import('@/views/payments/allocation-rules/index.vue'),
            meta: {
              pageTitle: 'allocationRule',
              permission: 'paymentallocation.admin.v1.rules.view'
            }
          },
          {
            path: 'allocation-rules/add',
            name: 'payments-allocation-rules-add',
            component: () => import('@/views/payments/allocation-rules/add.vue'),
            meta: {
              pageTitle: 'addAllocationRule',
              backLinkName: 'payments-allocation-rules',
              permission: 'paymentallocation.admin.v1.rules.create'
            },
          },
          {
            path: 'allocation-rules/:id/edit',
            name: 'payments-allocation-rules-edit',
            component: () => import('@/views/payments/allocation-rules/edit.vue'),
            meta: {
              pageTitle: 'editAllocationRule',
              backLinkName: 'payments-allocation-rules',
              permission: 'paymentallocation.admin.v1.rules.update'
            },
          },
          {
            path: 'allocation-rules/:id',
            name: 'payments-allocation-rules-detail',
            component: () => import('@/views/payments/allocation-rules/detail.vue'),
            meta: {
              pageTitle: 'allocationRuleDetail',
              backLinkName: 'payments-allocation-rules',
              permission: 'paymentallocation.admin.v1.rules.view'
            },
          },
          {
            path: 'return-requests',
            component: () => import('@/layouts/ReturnRequest.vue'),
            children: [
              {
                path: '',
                name: 'return-requests',
                component: () => import('@/views/return-requests/index.vue'),
                meta: {
                  pageTitle: 'allReturnRequests',
                  permission: 'order.admin.v1.return-requests.supervisor'
                },
              },
              {
                path: 'online-shopping',
                name: 'return-requests-online-shopping',
                component: () => import('@/views/return-requests/OnlineShopping.vue'),
                meta: {
                  pageTitle: 'onlineShoppingReturns',
                  permission: 'order.admin.v1.return-requests.online.view'
                },
              },
              {
                path: 'affiliate',
                name: 'return-requests-affiliate',
                component: () => import('@/views/return-requests/affiliate.vue'),
                meta: {
                  pageTitle: 'affiliatorReturns',
                  permission: 'order.admin.v1.return-requests.affiliate.view'
                },
              },
            ],
          },
          {
            path: 'withdrawal-requests',
            name: 'withdrawal-requests',
            component: () => import('@/views/payments/WithdrawalRequest.vue'),
            meta: {
              pageTitle: 'withdrawalRequests',
              permission: 'payment.admin.v1.wallets.withdrawal-requests.view'
            },
          },
          {
            path: 'bank-cards',
            name: 'bankCards',
            component: () => import('@/views/users/bank-cards.vue'),
            meta: {
              pageTitle: 'usersBankCards',
              permission: 'payment.admin.v1.user-cards.supervisor'
            },
          }
        ],
      },
      {
        path: '/users',
        children: [
          {
            path: '',
            name: 'users',
            component: () => import('@/views/users/Users.vue'),
            meta: {
              pageTitle: 'users',
              permission: 'user.admin.v1.users.supervisor'
            },
          },
          {
            path: ':id',
            component: () => import('@/layouts/UserProfile.vue'),
            children: [
              {
                path: '',
                name: 'users-overview',
                component: () => import('@/views/users/Overview/index.vue'),
                meta: {
                  permission: 'user.admin.v1.users.view'
                },
              },
              {
                path: 'sales-finance',
                name: 'sales-finance',
                component: () => import('@/views/users/Overview/SalesFinance.vue'),
                meta: {
                  permission: 'user.admin.v1.users.view'
                },
              },
              {
                path: 'meal-compliance',
                name: 'users-meal-compliance',
                component: () => import('@/views/users/MealCompliance.vue'),
                meta: {
                  permission: 'food.admin.v1.users.user-menu-day-meals.compliance.view'
                },
              }
            ],
          },
          {
            path: 'request-otp',
            component: () => import('@/views/users/RequestOtp.vue'),
            meta: {
              pageTitle: 'requestOtp',
              hideToolbar: true,
              permission: 'auth.admin.v1.users.generate-otp.update'
            },
          },
          {
            path: 'user-menus/:id',
            name: 'user-menus',
            component: () => import('@/views/users/UserMenus.vue'),
            meta: {
              pageTitle: 'userMenu',
              backLink: true,
              permission: 'food.admin.v1.user-menus.view'
            },
          }
        ],
      },
      {
        path: '/definitions',
        children: [
          {
            path: 'diseases',
            name: 'definitions-diseases',
            component: () => import('@/views/definitions/diseases/index.vue'),
            meta: {
              pageTitle: 'diseases',
              hideToolbar: true,
              allPermissions: ['food.admin.v1.diseases.view', 'food.admin.v1.diseases.create']
            },
          },
          {
            path: 'diseases/:parentId/subdiseases',
            name: 'subdiseases',
            component: () => import('@/views/definitions/diseases/subdiseases.vue'),
            meta: {
              pageTitle: 'subdiseases',
              allPermissions: ['food.admin.v1.diseases.view', 'food.admin.v1.diseases.create']
            },
          },
          {
            path: 'countries',
            name: 'definitions-countries',
            component: () => import('@/views/definitions/Countries.vue'),
            meta: {
              pageTitle: 'countries',
              permission: 'user.admin.v1.countries.view'
            },
          },
          {
            path: '/definitions/customer-origins',
            name: 'customer-origins',
            component: () => import('@/views/definitions/CustomerOrigins.vue'),
            meta: {
              pageTitle: 'customerOrigins',
              permission: 'behandam.admin.v1.customer-origins.view'
            },
          },
          {
            path: '/definitions/lead-distribution-rules',
            name: 'lead-distribution-rules',
            component: () => import('@/views/definitions/LeadDistribution.vue'),
            meta: {
              pageTitle: 'leadDistributionRules',
              permission: 'behandam.admin.v1.lead-distribution-rules.view'
            },
          },
          {
            path: '/definitions/tags',
            name: 'definitions-tags',
            component: () => import('@/views/definitions/Tags.vue'),
            meta: {
              pageTitle: 'tags',
              permission: 'categorization.admin.v1.tags.view'
            },
          },
          {
            path: '/definitions/units',
            name: 'units',
            component: () => import('@/views/definitions/Units.vue'),
            meta: {
              pageTitle: 'units',
              permission: 'food.admin.v1.units.view'
            },
          },
          {
            path: '/definitions/supportCallType',
            name: 'supportCallType',
            component: () => import('@/views/definitions/supportCallType.vue'),
            meta: {
              pageTitle: 'supportCallTypes',
              permission: 'behandam.admin.v1.support.call-types.view'
            },
          },
          {
            path: '/definitions/supportCallPattern',
            name: 'supportCallPattern',
            component: () => import('@/views/definitions/supportCallPattern.vue'),
            meta: {
              pageTitle: 'supportCallPatterns',
              permission: 'behandam.admin.v1.support.call-patterns.view'
            },
          },
          {
            path: '/definitions/commission-rules',
            name: 'commissionRules',
            component: () => import('@/views/definitions/commissionRule.vue'),
            meta: {
              pageTitle: 'commissionRules',
              permission: 'behandam.admin.v1.affiliates.commissions.rules.view'
            },
          },
          {
            path: '/definitions/conversation-topics',
            name: 'conversationTopics',
            component: () => import('@/views/definitions/conversationTopics.vue'),
            meta: {
              pageTitle: 'conversationTopics',
              permission: 'behandam.admin.v1.support.conversation-topics.view'
            },
          },
          {
            path: '/definitions/sms-templates',
            name: 'sms-templates',
            component: () => import('@/views/definitions/SmsTemplates.vue'),
            meta: {
              pageTitle: 'smsTemplates',
              allPermissions: ['notification.admin.v1.sms-templates.view', 'notification.admin.v1.sms-templates.create']
            },
          },
          {
            path: '/definitions/options',
            name: 'definitions-options',
            component: () => import('@/views/definitions/Options.vue'),
            meta: {
              pageTitle: 'options',
              permission: 'behandam.admin.v1.options.view'
            },
          },
          {
            path: '/definitions/calorie-formulas',
            name: 'calorie-formulas',
            component: () => import('@/views/definitions/calorieFormulas/CalorieFormulas.vue'),
            meta: {
              pageTitle: 'calorieFormulas',
              permission: 'behandam.admin.v1.calorie-formulas.view'
            },
          },
          {
            path: '/definitions/calorie-formulas/add',
            name: 'create-calorie-formula',
            component: () => import('@/views/definitions/calorieFormulas/AddCalorieFormula.vue'),
            meta: {
              pageTitle: 'addCalorieFormula',
              backLink: true,
              backLinkName: 'calorie-formulas',
              permission: 'behandam.admin.v1.calorie-formulas.create'
            },
          },
          {
            path: '/definitions/calorie-formulas/:id',
            name: 'edit-calorie-formula',
            component: () => import('@/views/definitions/calorieFormulas/EditCalorieFormula.vue'),
            meta: {
              pageTitle: 'editCalorieFormula',
              backLink: true,
              backLinkName: 'calorie-formulas',
              permission: 'behandam.admin.v1.calorie-formulas.update'
            },
          },
          {
            path: '/definitions/calorie-formulas/:id/view',
            name: 'view-calorie-formula',
            component: () => import('@/views/definitions/calorieFormulas/ViewCalorieFormula.vue'),
            meta: {
              pageTitle: 'viewCalorieFormula',
              backLink: true,
              backLinkName: 'calorie-formulas',
              permission: 'behandam.admin.v1.calorie-formulas.view'
            },
          },

        ],
      },
      {
        path: '/products',
        children: [
          {
            path: '/products',
            name: 'products',
            component: () => import('@/views/product/Products.vue'),
            meta: {
              pageTitle: 'products',
              permission: 'product.admin.v1.products.view'
            },
          },
          {
            path: '/products/create',
            name: 'create-product',
            component: () => import('@/views/product/AddProduct.vue'),
            meta: {
              pageTitle: 'createProduct',
              breadcrumbs: ['products', 'createProduct'],
              permission: 'product.admin.v1.products.create'
            },
          },
          {
            path: '/products/:id',
            name: 'edit-product',
            component: () => import('@/views/product/EditProduct.vue'),
            meta: {
              pageTitle: 'editProduct',
              breadcrumbs: ['products', 'editProduct'],
              permission: 'product.admin.v1.products.update'
            },
          },
          {
            path: '/products/types',
            name: 'product-types',
            component: () => import('@/views/product/product-type/ProductType.vue'),
            meta: {
              pageTitle: 'productTypes',
              permission: 'product.admin.v1.types.view'
            },
          },
          {
            path: '/products/types/:type/attributes',
            name: 'product-type-attributes',
            component: () => import('@/views/product/product-type/product-type-attribute/ProductTypeAttribute.vue'),
            meta: {
              pageTitle: 'attributes',
              permission: 'product.admin.v1.types.attributes.view'
            },
          },
        ],
      },
      {
        path: '/magazine',
        children: [
          {
            path: '/magazine/article-list',
            name: 'blogs',
            component: () => import('@/views/magazine/BlogsTabs.vue'),
            meta: {
              pageTitle: 'blogs',
              permission: 'content.admin.v1.posts.view'
            },
          },
          {
            path: 'tags',
            name: 'magazine-tags',
            component: () => import('@/views/magazine/Tags.vue'),
            meta: {
              pageTitle: 'tags',
              permission: 'categorization.admin.v1.tags.view'
            },
          },
          {
            path: '/magazine/article/:id',
            name: 'edit-blog',
            component: () => import('@/views/magazine/BlogForm.vue'),
            meta: {
              pageTitle: 'editBlog',
              backLink: true,
              backLinkName: 'blogs',
              permission: 'content.admin.v1.posts.update'
            },
          },
          {
            path: '/magazine/article/add',
            name: 'create-blog',
            component: () => import('@/views/magazine/BlogForm.vue'),
            meta: {
              pageTitle: 'addBlog',
              backLink: true,
              backLinkName: 'blogs',
              permission: 'content.admin.v1.posts.create'
            },
          },
          {
            path: 'category-list',
            name: 'categoryList',
            component: () => import('@/views/magazine/Category.vue'),
            meta: {
              pageTitle: 'categoryList',
              permission: 'categorization.admin.v1.categories.view'
            },
          },
          {
            path: 'category-list/:categoryType/:parentId',
            name: 'subcategories',
            component: () => import('@/views/categories/subcategories.vue'),
            meta: {
              pageTitle: 'subcategories',
              permission: 'categorization.admin.v1.categories.sub-categories.view'
            },
          },
          {
            path: 'comments',
            name: 'comments',
            component: () => import('@/views/magazine/Comments.vue'),
            meta: {
              pageTitle: 'کامنت ها',
              permission: 'feedback.admin.v1.comments.view'
            },
          },
        ],
      },
      {
        path: '/diet',
        children: [
          {
            path: '/diet/list',
            name: 'diets',
            component: () => import('@/views/diet/Diets.vue'),
            meta: {
              pageTitle: 'diets',
              permission: 'content.admin.v1.posts.view'
            },
          },
          {
            path: '/diet/:id',
            name: 'edit-diet',
            component: () => import('@/views/diet/EditDiet.vue'),
            meta: {
              pageTitle: 'editDiet',
              backLink: true,
              backLinkName: 'diets',
              permission: 'content.admin.v1.posts.update'
            },
          },
          {
            path: '/diet/add',
            name: 'create-diet',
            component: () => import('@/views/diet/AddDiet.vue'),
            meta: {
              pageTitle: 'addDiet',
              backLink: true,
              backLinkName: 'diets',
              permission: 'content.admin.v1.posts.create'
            },
          },
        ],
      },
      {
        path: '/diet-videos',
        children: [
          {
            path: '',
            name: 'diet-videos',
            component: () => import('@/views/diet-videos/DietVideos.vue'),
            meta: {
              pageTitle: 'dietVideos',
              permission: 'content.admin.v1.diet-video.view'
            },
          },
        ],
      },
      {
        path: '/dynamic-builder',
        children: [
          {
            path: 'layouts',
            name: 'dynamic-layouts',
            component: () => import('@/views/dynamic-builder/Layouts.vue'),
            meta: {
              pageTitle: 'layouts',
              permission: 'dynamicbuilder.admin.v1.layouts.view'
            },
          },
          {
            path: 'sections',
            name: 'dynamic-sections',
            component: () => import('@/views/dynamic-builder/Sections.vue'),
            meta: {
              pageTitle: 'sections',
              permission: 'dynamicbuilder.admin.v1.sections.view'
            },
          },
          {
            path: 'components',
            name: 'dynamic-components',
            component: () => import('@/views/dynamic-builder/Components.vue'),
            meta: {
              pageTitle: 'components',
              permission: 'dynamicbuilder.admin.v1.components.view'
            },
          },
          {
            path: 'flow-construction',
            name: 'flowConstruction',
            component: () => import('@/views/dynamic-builder/flowConstruction.vue'),
            meta: {
              pageTitle: 'flowConstruction',
              permission: 'dynamicbuilder.admin.v1.components.view'
            },
          },
        ],
      },
      {
        path: '/settings',
        children: [
          {
            path: 'app-version',
            name: 'settings-app-version',
            component: () => import('@/views/setting/AppVersions.vue'),
            meta: {
              pageTitle: 'appVersion',
              permission: 'setting.v1.releases.view'
            },
          },
          {
            path: 'user-features',
            name: 'settings-user-features',
            component: () => import('@/views/setting/UserFeatures.vue'),
            meta: {
              pageTitle: 'betaFeatures',
              permission: 'user.admin.v1.features.view'
            },
          },
          {
            path: 'changelog',
            name: 'settings-changelog',
            component: () => import('@/views/setting/Changelog.vue'),
            meta: {
              pageTitle: 'changelog',
              permission: ''
            },
          },
        ],
      },
      {
        path: '/profile',
        component: () => import('@/layouts/Profile.vue'),
        children: [
          {
            path: '',
            name: 'profile',
            component: () => import('@/views/profile/Overview.vue'),
            meta: {
              pageTitle: 'overview',
              permission: ''
            },
          },
          {
            path: 'settings',
            name: 'profile-settings',
            component: () => import('@/views/profile/Settings.vue'),
            meta: {
              pageTitle: 'settings',
              permission: ''
            },
          },
          {
            path: 'activities',
            name: 'admin-activities',
            component: () => import('@/views/profile/Activitys.vue'),
            meta: {
              pageTitle: 'activities',
              permission: ''
            },
          }

        ],
      },
      {
        path: '/media',
        name: 'fileManagement',
        component: () => import('@/views/media/Media.vue'),
        meta: {
          pageTitle: 'File Management',
          hideToolbar: true,
          permission: 'media.admin.v1.media.view'
        },
      },
      {
        path: '/food-replacement/:id',
        name: 'foodReplacement',
        component: () => import('@/views/food-replacement/index.vue'),
        meta: {
          pageTitle: 'foodReplacement',
          permission: ''
        },
      },
      {
        path: '/tenant',
        name: 'tenant',
        component: () => import('@/views/tenant/Tenant.vue'),
        meta: {
          pageTitle: 'tenant',
          breadcrumbs: ['tenant'],
          permission: 'tenant.admin.v1.tenants.view'
        },
      },
      {
        path: '/personnel',
        name: 'personnel',
        component: () => import('@/views/personnel/Personnels.vue'),
        meta: {
          pageTitle: 'personnels',
          permission: 'user.admin.v1.admins.supervisor'
        },
      },
      {
        path: '/departments',
        name: 'departments',
        component: () => import('@/views/department/Departments.vue'),
        meta: {
          pageTitle: 'departments',
          allPermissions: ['auth.admin.v1.departments.view', 'auth.admin.v1.departments.create']
        },
      },
      {
        path: '/personnel/:id',
        component: () => import('@/layouts/PersonnelProfile.vue'),
        children: [
          {
            path: '/personnel/:id',
            name: 'personnel-profile',
            component: () => import('@/views/personnel/Personnel.vue'),
            meta: {
              pageTitle: 'personnelProfile',
              backLink: true,
              backLinkName: 'personnel',
              permission: 'user.admin.v1.admins.supervisor'
            },
          },
          {
            path: '/personnel/:id/roles',
            name: 'personnel-role',
            component: () => import('@/views/personnel/PersonnelRole.vue'),
            meta: {
              pageTitle: 'rolesPermissions',
              backLink: true,
              backLinkName: 'personnel',
              permission: 'user.admin.v1.admins.supervisor'
            },
          },
          {
            path: '/personnel/:id/change-password',
            name: 'personnel-change-password',
            component: () => import('@/views/personnel/PersonnelChangePassword.vue'),
            meta: {
              pageTitle: 'changePassword',
              backLink: true,
              backLinkName: 'personnel',
              permission: 'user.admin.v1.admins.supervisor'
            },
          },
        ],
      },
      {
        path: '/roles',
        name: 'roles',
        component: () => import('@/views/roles/Roles.vue'),
        meta: {
          pageTitle: 'roles',
          breadcrumbs: ['roles'],
          permission: 'auth.admin.v1.roles.view'
        },
      },
      {
        path: '/permissions',
        name: 'permissions',
        component: () => import('@/views/permissions/Permissions.vue'),
        meta: {
          pageTitle: 'permissions',
          breadcrumbs: ['permissions'],
          permission: 'auth.admin.v1.permissions.view'
        },
      },
      {
        path: '/tickets',
        children: [
          {
            path: '',
            name: 'tickets',
            component: () => import('@/views/tickets/Tickets.vue'),
            meta: {
              pageTitle: 'tickets',
              breadcrumbs: ['tickets'],
              permission: 'ticket.admin.v1.tickets.manage'
            },
          },
          {
            path: 'mine',
            name: 'my-tickets',
            component: () => import('@/views/tickets/MyTickets.vue'),
            meta: {
              pageTitle: 'myTickets',
              breadcrumbs: ['tickets', 'myTickets'],
              permission: 'ticket.admin.v1.tickets.view'
            },
          },
          {
            path: 'distribution-list',
            name: 'distributionList',
            component: () => import('@/views/tickets/DistributionList.vue'),
            meta: {
              pageTitle: 'distributionList',
              hideToolbar: true,
              permission: 'behandam.admin.v1.distribution.strategies.view'
            },
          },
          {
            path: 'tags',
            name: 'tickets.tags',
            component: () => import('@/views/tickets/Tags.vue'),
            meta: {
              pageTitle: 'tags',
              permission: 'categorization.admin.v1.tags.view'
            },
          },
        ],
      },
      {
        path: '/wallets',
        name: 'wallets',
        component: () => import('@/views/wallet/Wallets.vue'),
        meta: {
          pageTitle: 'wallets',
          permission: 'payment.admin.v1.wallets.supervisor'
        },
      },
      {
        path: '/wallets/transaction-logs',
        name: 'transaction-logs',
        component: () => import('@/views/wallet/TransactionLogs.vue'),
        meta: {
          pageTitle: 'transactionLogs',
          permission: 'payment.admin.v1.wallets.transaction-logs.view'
        },
      },
      {
        path: '/calculate/weight',
        name: 'calculate-weight',
        component: () => import('@/views/calculate/calculateWeight.vue'),
        meta: {
          pageTitle: 'calculateWeight',
          breadcrumbs: ['calculateWeight'],
          permission: 'behandam.admin.v1.body-metrics.view'
        },
      },
      {
        path: '/faqs',
        children: [
          {
            path: '',
            name: 'faqs',
            component: () => import('@/views/faq/Faqs.vue'),
            meta: {
              pageTitle: 'faqs',
              permission: 'content.admin.v1.faqs.view'
            },
          },
          {
            path: 'category',
            name: 'faqs-category',
            component: () => import('@/views/faq/Category.vue'),
            meta: {
              pageTitle: 'category',
              permission: 'categorization.admin.v1.categories.view'
            },
          }
        ],
      },
      {
        path: '/transactions',
        name: 'transactions',
        component: () => import('@/views/transaction/Transactions.vue'),
        meta: {
          pageTitle: 'transactions',
          permission: 'payment.admin.v1.wallets.transactions.supervisor'
        },
      },
      {
        path: '/transactions/pending',
        name: 'pendingTransactions',
        component: () => import('@/views/transaction/PendingTransactions.vue'),
        meta: {
          pageTitle: 'pendingTransactions',
          permission: 'payment.admin.v1.wallets.transactions.supervisor'
        },
      },
      {
        path: '/categories',
        name: 'categories',
        component: () => import('@/views/categories/index.vue'),
        meta: {
          pageTitle: 'categories',
          permission: 'categorization.admin.v1.categories.view'
        },
      },
      {
        path: '/tags',
        name: 'tags',
        component: () => import('@/views/tags/index.vue'),
        meta: {
          pageTitle: 'categories',
          permission: 'categorization.admin.v1.tags.view'
        },
      },
      {
        path: '/affiliate-links',
        name: 'affiliateLinks',
        component: () => import('@/views/affiliate-link/index.vue'),
        meta: {
          pageTitle: 'affiliateLinks',
          permission: 'behandam.admin.v1.affiliates.links.view'
        },
      },
      {
        path: '/affiliate-partner',
        name: 'affiliatesPartners',
        component: () => import('@/views/affiliate-partner/index.vue'),
        meta: {
          pageTitle: 'affiliatesPartners',
          permission: 'behandam.admin.v1.affiliates.partners.view'
        },
      },
      {
        path: '/affiliate-partner/external',
        name: 'affiliatesPartnersExternal',
        component: () => import('@/views/affiliate-partner/external.vue'),
        meta: {
          pageTitle: 'affiliatesPartnersExternal',
          permission: 'behandam.admin.v1.affiliates.partners.external.view'
        },
      },
      {
        path: '/reports/sales-associates',
        name: 'associateSalesReport',
        component: () => import('@/views/reports/salesAssociates.vue'),
        meta: {
          pageTitle: 'associateSalesReport',
          permission: 'report.admin.v1.sales.affiliate.view'
        },
      },
      {
        path: '/reports/affiliate-sales-associates',
        name: 'affiliateSalesReport',
        component: () => import('@/views/reports/AffiliateSalesAssociates/index.vue'),
        meta: {
          pageTitle: 'affiliateSalesReport',
          permission: 'report.admin.v1.sales.affiliate.view'
        }
      },
      {
        path: '/reports/affiliate-sales-associates-external',
        name: 'affiliateSalesReportExternal',
        component: () => import('@/views/reports/affiliateSalesAssociatesExternal.vue'),
        meta: {
          pageTitle: 'affiliateSalesReportExternal',
          permission: 'report.admin.v1.sales.affiliate.external.view'
        }
      },
      {
        path: '/reports/affiliate-sales-details',
        name: 'affiliateSalesReportDetails',
        component: () => import('@/views/reports/AffiliateDetails.vue'),
        meta: {
          pageTitle: 'affiliateSalesReportDetails',
          permission: 'report.admin.v1.sales.affiliate.view'
        }
      },
      {
        path: '/reports/affiliate-links-report',
        name: 'affiliateLinksReport',
        component: () => import('@/views/reports/AffiliateLinks.vue'),
        meta: {
          pageTitle: 'گزارش لینک‌های افیلیت',
          permission: 'report.admin.v1.affiliate-links.total.view'
        }
      },
      {
        path: '/reports/requested-days',
        name: 'requestedDaysReport',
        component: () => import('@/views/reports/RequestedDays.vue'),
        meta: {
          pageTitle: 'گزارش روزهای درخواستی',
          permission: 'report.admin.v1.requested-days.total.view'
        }
      },
      {
        path: '/reports/input-channels',
        name: 'inputChannelsReport',
        component: () => import('@/views/reports/InputChannels.vue'),
        meta: {
          pageTitle: 'گزارش کانال‌های ورودی',
          permission: 'report.admin.v1.sales.top-customer-origins.view'
        }
      },
      {
        path: '/reports/sales-products-members',
        name: 'salesProductsMembersReport',
        component: () => import('@/views/reports/SalesProductsMembers.vue'),
        meta: {
          pageTitle: 'گزارش فروش محصولات',
          permission: 'report.admin.v1.sales.products.members.view'
        },
      },
      {
        path: '/reports/financial',
        name: 'financialReport',
        component: () => import('@/views/reports/Financial.vue'),
        meta: {
          pageTitle: 'گزارش مالی',
          permission: 'report.admin.v1.financial.daily.view'
        },
      },
      {
        path: '/reports/wallet/total',
        name: 'walletTotalReport',
        component: () => import('../views/reports/Wallet.vue'),
        meta: {
          pageTitle: 'گزارش کیف پول',
          permission: 'report.admin.v1.wallets.total.view'
        },
      },
      {
        path: '/commissions/settlements',
        name: 'commissionSettlement',
        component: () => import('@/views/commission-settlement/CommissionSettlement.vue'),
        meta: {
          pageTitle: 'commissions',
          permission: 'behandam.admin.v1.affiliates.commissions.settlements.view'
        },
      },
      {
        path: '/reports/old-software-migration',
        name: 'oldSoftwareMigration',
        component: () => import('@/views/old-software-migration/index.vue'),
        meta: {
          pageTitle: 'oldSoftwareMigration',
          permission: 'behandam.admin.v1.old-software-migration.stats.view'
        },
      },
      {
        path: '/distribution-strategies',
        name: 'distributionStrategies',
        component: () => import('@/views/distribution-strategies/index.vue'),
        meta: {
          pageTitle: 'distributionStrategies',
          hideToolbar: true,
          permission: 'behandam.admin.v1.distribution.strategies.view'
        },
      }
    ],
  },
  {
    path: '/',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: '/sign-in',
        name: 'sign-in',
        component: () => import('@/views/auth/SignIn.vue'),
        meta: {
          pageTitle: 'Sign In',
        },
      }
    ],
  },
  {
    path: '/403',
    name: '403',
    component: () => import('@/views/403.vue'),
    meta: {
      pageTitle: '403',
    },
  },
  {
    path: '/404',
    name: '404',
    component: () => import('@/views/404.vue'),
    meta: {
      pageTitle: '404',
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      return {
        el: to.hash,
        top: 80,
        behavior: 'smooth',
      };
    }
    return {
      top: 0,
      left: 0,
      behavior: 'smooth',
    };
  },
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const configStore = useConfigStore();
  const { hasPermission, hasAllPermissions } = usePermissions();

  document.title = `${to.meta.pageTitle} - ${import.meta.env.VITE_APP_NAME}`;

  configStore.resetLayoutConfig();

  if (to.meta.middleware === 'auth') {
    if (!authStore.isAuthenticated) {
      return next({ name: 'sign-in' });
    }

    const permission = to.meta.permission as string | string[] | undefined;
    if (permission && !hasPermission(permission)) {
      if (to.name === 'advanced-dashboard') {
        return next({ name: 'dashboard' });
      }
      return next({ name: '403' });
    }

    const allPermissions = to.meta.allPermissions as string[] | undefined;
    if (allPermissions && !hasAllPermissions(allPermissions)) {
      return next({ name: '403' });
    }
  }

  next();
});

export default router;
